import type {
  DocumentFieldDefinition,
  DocumentSectionDefinition,
} from '@/features/case-workspace/types'

const ISO_DATE_RULE = {
  type: 'pattern' as const,
  value: '^\\d{4}-\\d{2}-\\d{2}$',
  message: 'Use YYYY-MM-DD format.',
}

const NUMERIC_RULE = {
  type: 'pattern' as const,
  value: '^-?\\d+(\\.\\d+)?$',
  message: 'Use a numeric value.',
}

const CURRENCY_RULE = {
  type: 'pattern' as const,
  value: '^[A-Z]{3}$',
  message: 'Use a three-letter currency code such as USD or EUR.',
}

const IDENTIFIER_RULE = {
  type: 'pattern' as const,
  value: '^[A-Z0-9\\-\\/]{2,40}$',
  message: 'Use letters, numbers, dashes, or slashes.',
}

const YES_NO_OPTIONS = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
]

function field(definition: DocumentFieldDefinition) {
  return definition
}

export const INVOICE_TYPE_OPTIONS = [
  { value: 'commercial_invoice', label: 'Commercial invoice' },
  { value: 'pro_forma_invoice', label: 'Pro forma invoice' },
  { value: 'other_supporting_document', label: 'Other invoice / supporting document' },
]

export const INVOICE_DOCUMENT_SOURCE_OPTIONS = [
  { value: 'supplier_provided', label: 'Supplier-provided' },
  { value: 'broker_provided', label: 'Broker-provided' },
  { value: 'importer_provided', label: 'Importer-provided' },
  { value: 'uploaded_scan', label: 'Uploaded scan' },
  { value: 'ace_electronic_source', label: 'ACE / electronic source' },
  { value: 'unknown_needs_review', label: 'Unknown / Needs Review' },
]

export const INVOICE_TRANSACTION_TYPE_OPTIONS = [
  { value: 'sale_purchase', label: 'Sale / purchase' },
  { value: 'non_purchase_shipment', label: 'Non-purchase shipment' },
  { value: 'consignment', label: 'Consignment' },
  { value: 'sample', label: 'Sample' },
  { value: 'return', label: 'Return' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'transfer', label: 'Transfer' },
]

export const INVOICE_PAYMENT_METHOD_OPTIONS = [
  { value: 'wire', label: 'Wire' },
  { value: 'ach', label: 'ACH' },
  { value: 'letter_of_credit', label: 'Letter of credit' },
  { value: 'open_account', label: 'Open account' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
]

export const INVOICE_RECONCILIATION_STATUS_OPTIONS = [
  { value: 'exact_match', label: 'Exact match' },
  { value: 'likely_match', label: 'Likely match' },
  { value: 'missing_from_other_document', label: 'Missing from one document' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'needs_manual_review', label: 'Needs manual review' },
]

export const INVOICE_RISK_OPTIONS = [
  { value: 'informational', label: 'Informational' },
  { value: 'low', label: 'Low risk' },
  { value: 'moderate', label: 'Moderate risk' },
  { value: 'high', label: 'High risk' },
  { value: 'blocks_acceptance', label: 'Blocks acceptance / transmit' },
]

export const INVOICE_CUSTOMS_VALUE_BASIS_OPTIONS = [
  { value: 'purchase_price', label: 'Purchase price' },
  { value: 'non_purchase_value', label: 'Value for non-purchase shipment' },
  { value: 'declared_customs_value', label: 'Declared customs value' },
]

export const COMMERCIAL_INVOICE_SECTIONS: DocumentSectionDefinition[] = [
  {
    id: 'invoice_overview',
    title: 'Invoice Overview',
    description: 'Capture the invoice identity, source, and the baseline information required for customs review under 19 CFR Part 141.',
    fields: [
      field({
        key: 'invoice_number',
        label: 'Invoice Number',
        officialName: 'Invoice Number',
        type: 'text',
        required: true,
        aceMappingId: 'invoice.invoice_number',
        validation: [{ type: 'required', message: 'Invoice number is required.' }],
      }),
      field({
        key: 'invoice_date',
        label: 'Invoice Date',
        officialName: 'Date of Invoice',
        type: 'date',
        required: true,
        aceMappingId: 'invoice.invoice_date',
        validation: [
          { type: 'required', message: 'Invoice date is required.' },
          ISO_DATE_RULE,
        ],
      }),
      field({
        key: 'invoice_type',
        label: 'Invoice Type',
        officialName: 'Invoice Type',
        type: 'select',
        options: INVOICE_TYPE_OPTIONS,
        required: true,
        aceMappingId: 'invoice.invoice_type',
        validation: [{ type: 'required', message: 'Invoice type is required.' }],
      }),
      field({
        key: 'document_source',
        label: 'Document Source',
        officialName: 'Document Source',
        type: 'select',
        options: INVOICE_DOCUMENT_SOURCE_OPTIONS,
        aceMappingId: 'invoice.document_source',
      }),
      field({
        key: 'source_file_name',
        label: 'Source File Name',
        officialName: 'Source File Name',
        type: 'text',
        aceMappingId: 'invoice.source_file_name',
      }),
      field({
        key: 'port_of_entry_destination',
        label: 'Port of Entry to Which Merchandise Is Destined',
        officialName: 'Port of Entry to Which Merchandise Is Destined',
        type: 'text',
        aceMappingId: 'invoice.port_of_entry_destination',
        helpText: '19 CFR § 141.86(a)(1) requires the port of entry to which the merchandise is destined.',
      }),
      field({
        key: 'reference_numbers',
        label: 'Reference Number(s)',
        officialName: 'Reference Number(s)',
        type: 'textarea',
        aceMappingId: 'invoice.reference_numbers',
      }),
    ],
  },
  {
    id: 'transaction_terms',
    title: 'Transaction & Terms',
    description: 'Capture the sale or non-sale context, transaction terms, payment details, and the customs value basis required for review.',
    fields: [
      field({
        key: 'purchase_order_number',
        label: 'Purchase Order Number',
        officialName: 'Purchase Order Number',
        type: 'text',
        aceMappingId: 'invoice.purchase_order_number',
      }),
      field({
        key: 'sales_order_number',
        label: 'Sales Order Number',
        officialName: 'Sales Order Number',
        type: 'text',
        aceMappingId: 'invoice.sales_order_number',
      }),
      field({
        key: 'contract_number',
        label: 'Contract Number',
        officialName: 'Contract Number',
        type: 'text',
        aceMappingId: 'invoice.contract_number',
      }),
      field({
        key: 'terms_of_sale',
        label: 'Terms of Sale',
        officialName: 'Terms of Sale',
        type: 'text',
        aceMappingId: 'invoice.terms_of_sale',
      }),
      field({
        key: 'incoterms',
        label: 'Incoterms',
        officialName: 'Incoterms',
        type: 'text',
        aceMappingId: 'invoice.incoterms',
      }),
      field({
        key: 'payment_terms',
        label: 'Payment Terms',
        officialName: 'Payment Terms',
        type: 'text',
        aceMappingId: 'invoice.payment_terms',
      }),
      field({
        key: 'payment_method',
        label: 'Payment Method',
        officialName: 'Payment Method',
        type: 'select',
        options: INVOICE_PAYMENT_METHOD_OPTIONS,
        aceMappingId: 'invoice.payment_method',
      }),
      field({
        key: 'related_party_transaction_indicator',
        label: 'Related-Party Transaction Indicator',
        officialName: 'Related-Party Transaction Indicator',
        type: 'select',
        options: YES_NO_OPTIONS,
        aceMappingId: 'invoice.related_party_transaction_indicator',
      }),
      field({
        key: 'customs_transaction_type',
        label: 'Customs Transaction Type',
        officialName: 'Customs Transaction Type',
        type: 'select',
        options: INVOICE_TRANSACTION_TYPE_OPTIONS,
        required: true,
        aceMappingId: 'invoice.customs_transaction_type',
        validation: [{ type: 'required', message: 'Customs transaction type is required.' }],
      }),
      field({
        key: 'customs_value_basis',
        label: 'Customs Value Basis',
        officialName: 'Customs Value Basis',
        type: 'select',
        options: INVOICE_CUSTOMS_VALUE_BASIS_OPTIONS,
        aceMappingId: 'invoice.customs_value_basis',
      }),
      field({
        key: 'currency_code',
        label: 'Currency',
        officialName: 'Kind of Currency',
        type: 'text',
        required: true,
        placeholder: 'USD',
        aceMappingId: 'invoice.currency_code',
        helpText: '19 CFR § 141.86(a)(7) requires the kind of currency. VICE normalizes this to a three-letter currency code.',
        validation: [
          { type: 'required', message: 'Currency is required.' },
          CURRENCY_RULE,
        ],
      }),
    ],
  },
  {
    id: 'seller_buyer_parties',
    title: 'Seller / Buyer / Parties',
    description: 'Track the seller, exporter, manufacturer, buyer, importer, consignee, and other parties needed to understand the customs transaction and cross-document relationships.',
    fields: [
      field({ key: 'seller_name', label: 'Seller Name', officialName: 'Person by Whom Sold', type: 'text', required: true, aceMappingId: 'invoice.seller_name', validation: [{ type: 'required', message: 'Seller name is required.' }] }),
      field({ key: 'seller_address', label: 'Seller Address', officialName: 'Seller Address', type: 'textarea', aceMappingId: 'invoice.seller_address' }),
      field({ key: 'exporter_name', label: 'Exporter Name', officialName: 'Exporter Name', type: 'text', aceMappingId: 'invoice.exporter_name' }),
      field({ key: 'exporter_address', label: 'Exporter Address', officialName: 'Exporter Address', type: 'textarea', aceMappingId: 'invoice.exporter_address' }),
      field({ key: 'manufacturer_name', label: 'Manufacturer / Supplier Name', officialName: 'Manufacturer / Supplier', type: 'text', aceMappingId: 'invoice.manufacturer_name' }),
      field({ key: 'manufacturer_address', label: 'Manufacturer / Supplier Address', officialName: 'Manufacturer / Supplier Address', type: 'textarea', aceMappingId: 'invoice.manufacturer_address' }),
      field({ key: 'buyer_name', label: 'Buyer Name', officialName: 'Person to Whom Sold', type: 'text', required: true, aceMappingId: 'invoice.buyer_name', validation: [{ type: 'required', message: 'Buyer name is required.' }] }),
      field({ key: 'buyer_address', label: 'Buyer Address', officialName: 'Buyer Address', type: 'textarea', aceMappingId: 'invoice.buyer_address' }),
      field({ key: 'importer_of_record_name', label: 'Importer of Record', officialName: 'Importer of Record', type: 'text', aceMappingId: 'invoice.importer_of_record_name' }),
      field({ key: 'importer_of_record_address', label: 'Importer Address', officialName: 'Importer Address', type: 'textarea', aceMappingId: 'invoice.importer_of_record_address' }),
      field({ key: 'consignee_name', label: 'Consignee', officialName: 'Consignee', type: 'text', aceMappingId: 'invoice.consignee_name' }),
      field({ key: 'consignee_address', label: 'Consignee Address', officialName: 'Consignee Address', type: 'textarea', aceMappingId: 'invoice.consignee_address' }),
      field({ key: 'ultimate_consignee_name', label: 'Ultimate Consignee', officialName: 'Ultimate Consignee', type: 'text', aceMappingId: 'invoice.ultimate_consignee_name' }),
      field({ key: 'notify_party_name', label: 'Notify Party', officialName: 'Notify Party', type: 'text', aceMappingId: 'invoice.notify_party_name' }),
      field({ key: 'ship_to_party', label: 'Ship-To Party', officialName: 'Ship-To Party', type: 'text', aceMappingId: 'invoice.ship_to_party' }),
      field({ key: 'broker_filer_name', label: 'Broker / Filer Name', officialName: 'Broker / Filer Name', type: 'text', aceMappingId: 'invoice.broker_filer_name' }),
      field({ key: 'contact_names', label: 'Contact Name(s)', officialName: 'Contact Name(s)', type: 'textarea', aceMappingId: 'invoice.contact_names' }),
      field({ key: 'contact_phone_email', label: 'Contact Phone / Email', officialName: 'Contact Phone / Email', type: 'textarea', aceMappingId: 'invoice.contact_phone_email' }),
      field({ key: 'responsible_employee_name', label: 'Responsible Employee of Exporter', officialName: 'Responsible Employee of Exporter', type: 'text', aceMappingId: 'invoice.responsible_employee_name', helpText: '19 CFR § 141.86(j) requires the invoice to identify by name a responsible employee of the exporter who has knowledge of the transaction.' }),
    ],
  },
  {
    id: 'shipment_routing',
    title: 'Shipment & Routing',
    description: 'Capture export, routing, carrier, bill, container, and port details needed for customs review and reconciliation.',
    fields: [
      field({ key: 'country_of_export', label: 'Country of Export', officialName: 'Country of Exportation', type: 'text', aceMappingId: 'invoice.country_of_export' }),
      field({ key: 'country_of_origin', label: 'Country of Origin', officialName: 'Country of Origin', type: 'text', aceMappingId: 'invoice.country_of_origin', helpText: '19 CFR § 141.86(a)(10) requires the country of origin of the merchandise.' }),
      field({ key: 'port_of_loading', label: 'Port of Loading', officialName: 'Port of Loading', type: 'text', aceMappingId: 'invoice.port_of_loading' }),
      field({ key: 'foreign_port_of_lading', label: 'Foreign Port of Lading', officialName: 'Port of Exportation / Foreign Port of Lading', type: 'text', aceMappingId: 'invoice.foreign_port_of_lading' }),
      field({ key: 'port_of_discharge', label: 'Port of Discharge / Unlading', officialName: 'Port of Discharge / Unlading', type: 'text', aceMappingId: 'invoice.port_of_discharge' }),
      field({ key: 'final_destination', label: 'Final Destination', officialName: 'Final Destination', type: 'text', aceMappingId: 'invoice.final_destination' }),
      field({ key: 'shipment_date', label: 'Shipment Date', officialName: 'Shipment Date', type: 'date', aceMappingId: 'invoice.shipment_date', validation: [ISO_DATE_RULE] }),
      field({ key: 'export_date', label: 'Export Date', officialName: 'Export Date', type: 'date', aceMappingId: 'invoice.export_date', validation: [ISO_DATE_RULE] }),
      field({ key: 'departure_date', label: 'Departure Date', officialName: 'Departure Date', type: 'date', aceMappingId: 'invoice.departure_date', validation: [ISO_DATE_RULE] }),
      field({ key: 'arrival_date', label: 'Arrival Date', officialName: 'Arrival Date', type: 'date', aceMappingId: 'invoice.arrival_date', validation: [ISO_DATE_RULE] }),
      field({ key: 'carrier_name', label: 'Carrier Name', officialName: 'Carrier Name', type: 'text', aceMappingId: 'invoice.carrier_name' }),
      field({ key: 'conveyance_identifier', label: 'Vessel / Flight / Truck / Rail Identifier', officialName: 'Conveyance Identifier', type: 'text', aceMappingId: 'invoice.conveyance_identifier' }),
      field({ key: 'mode_of_transport', label: 'Mode of Transport', officialName: 'Mode of Transport', type: 'text', aceMappingId: 'invoice.mode_of_transport' }),
      field({ key: 'bill_of_lading_number', label: 'Bill of Lading Number', officialName: 'Bill of Lading Number', type: 'text', aceMappingId: 'invoice.bill_of_lading_number', validation: [IDENTIFIER_RULE] }),
      field({ key: 'air_waybill_number', label: 'Air Waybill Number', officialName: 'Air Waybill Number', type: 'text', aceMappingId: 'invoice.air_waybill_number', validation: [IDENTIFIER_RULE] }),
      field({ key: 'house_bill_number', label: 'House Bill Number', officialName: 'House Bill Number', type: 'text', aceMappingId: 'invoice.house_bill_number', validation: [IDENTIFIER_RULE] }),
      field({ key: 'master_bill_number', label: 'Master Bill Number', officialName: 'Master Bill Number', type: 'text', aceMappingId: 'invoice.master_bill_number', validation: [IDENTIFIER_RULE] }),
      field({ key: 'container_numbers', label: 'Container Number(s)', officialName: 'Container Number(s)', type: 'text', aceMappingId: 'invoice.container_numbers' }),
      field({ key: 'seal_numbers', label: 'Seal Number(s)', officialName: 'Seal Number(s)', type: 'text', aceMappingId: 'invoice.seal_numbers' }),
    ],
  },
  {
    id: 'merchandise_lines',
    title: 'Merchandise Lines',
    description: 'Review invoice merchandise as structured line items instead of OCR blobs so descriptions, values, package details, and quantity logic can be validated.',
    kind: 'line_items',
    fields: [
      field({
        key: 'invoice_lines_registry',
        label: 'Invoice lines registry',
        type: 'text',
        editable: false,
        aceMappingId: 'invoice.line_items',
        helpText: 'Rendered through the dedicated commercial invoice line-item editor.',
      }),
    ],
  },
  {
    id: 'valuation_charges',
    title: 'Valuation & Charges',
    description: 'Capture purchase price, non-purchase value, currency, charges, deductions, and invoice totals needed for customs valuation review.',
    fields: [
      field({ key: 'purchase_price_statement', label: 'Purchase Price Statement', officialName: 'Purchase Price of Each Item', type: 'textarea', aceMappingId: 'invoice.purchase_price_statement', helpText: '19 CFR § 141.86(a)(5) requires the purchase price of each item when imported pursuant to sale or agreement to purchase.' }),
      field({ key: 'non_purchase_value_statement', label: 'Value Statement for Non-Purchase Shipment', officialName: 'Value for Each Item When Not Pursuant to Purchase', type: 'textarea', aceMappingId: 'invoice.non_purchase_value_statement', helpText: '19 CFR § 141.86(a)(6) requires value information when the merchandise is shipped otherwise than in pursuance of a purchase.' }),
      field({ key: 'freight_charge', label: 'Freight', officialName: 'Freight', type: 'number', aceMappingId: 'invoice.freight_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'insurance_charge', label: 'Insurance', officialName: 'Insurance', type: 'number', aceMappingId: 'invoice.insurance_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'commission_charge', label: 'Commission', officialName: 'Commission', type: 'number', aceMappingId: 'invoice.commission_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'cases_charge', label: 'Cases', officialName: 'Cases', type: 'number', aceMappingId: 'invoice.cases_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'containers_charge', label: 'Containers', officialName: 'Containers', type: 'number', aceMappingId: 'invoice.containers_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'coverings_charge', label: 'Coverings', officialName: 'Coverings', type: 'number', aceMappingId: 'invoice.coverings_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'packing_cost', label: 'Packing Costs', officialName: 'Cost of Packing', type: 'number', aceMappingId: 'invoice.packing_cost', validation: [NUMERIC_RULE] }),
      field({ key: 'inland_freight_charge', label: 'Inland Freight', officialName: 'Inland Freight', type: 'number', aceMappingId: 'invoice.inland_freight_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'international_freight_charge', label: 'International Freight', officialName: 'International Freight', type: 'number', aceMappingId: 'invoice.international_freight_charge', validation: [NUMERIC_RULE] }),
      field({ key: 'other_itemized_charges', label: 'Other Itemized Charges', officialName: 'Other Itemized Charges', type: 'textarea', aceMappingId: 'invoice.other_itemized_charges' }),
      field({ key: 'discounts_rebates', label: 'Discount / Rebate Fields', officialName: 'Discounts, Rebates, Drawbacks, and Bounties', type: 'textarea', aceMappingId: 'invoice.discounts_rebates', helpText: '19 CFR § 141.86(a)(9) and (g) require discounts and rebates to be set out in detail.' }),
      field({ key: 'royalties_license_fees', label: 'Royalties / License Fees', officialName: 'Royalties / License Fees', type: 'number', aceMappingId: 'invoice.royalties_license_fees', validation: [NUMERIC_RULE] }),
      field({ key: 'other_additions_deductions', label: 'Other Additions / Deductions', officialName: 'Other Additions / Deductions', type: 'textarea', aceMappingId: 'invoice.other_additions_deductions' }),
      field({ key: 'total_invoice_value', label: 'Total Invoice Value', officialName: 'Total Invoice Value', type: 'number', required: true, aceMappingId: 'invoice.total_invoice_value', validation: [{ type: 'required', message: 'Total invoice value is required.' }, NUMERIC_RULE] }),
      field({ key: 'total_charges', label: 'Total Charges', officialName: 'Total Charges', type: 'number', aceMappingId: 'invoice.total_charges', validation: [NUMERIC_RULE] }),
      field({ key: 'customs_value_declared', label: 'Customs Value if Separately Declared', officialName: 'Customs Value', type: 'number', aceMappingId: 'invoice.customs_value_declared', validation: [NUMERIC_RULE] }),
      field({ key: 'included_excluded_charges_indicator', label: 'Included vs Excluded Charges Indicator', officialName: 'Included / Excluded Charges Indicator', type: 'text', aceMappingId: 'invoice.included_excluded_charges_indicator' }),
    ],
  },
  {
    id: 'certifications_attachments',
    title: 'Certifications / Attachments',
    description: 'Track declarations, signatures, and attached support that affect customs completeness or valuation review.',
    fields: [
      field({ key: 'signatory_name', label: 'Signatory Name', officialName: 'Signatory Name', type: 'text', aceMappingId: 'invoice.signatory_name' }),
      field({ key: 'signatory_title', label: 'Signatory Title', officialName: 'Signatory Title', type: 'text', aceMappingId: 'invoice.signatory_title' }),
      field({ key: 'signature_present_indicator', label: 'Signature Present Indicator', officialName: 'Signature Present Indicator', type: 'select', options: YES_NO_OPTIONS, aceMappingId: 'invoice.signature_present_indicator' }),
      field({ key: 'date_signed', label: 'Date Signed', officialName: 'Date Signed', type: 'date', aceMappingId: 'invoice.date_signed', validation: [ISO_DATE_RULE] }),
      field({ key: 'declaration_text_present', label: 'Declaration Text Present', officialName: 'Declaration Text Present', type: 'select', options: YES_NO_OPTIONS, aceMappingId: 'invoice.declaration_text_present' }),
      field({ key: 'certification_notes', label: 'Certification Notes', officialName: 'Certification Notes', type: 'textarea', aceMappingId: 'invoice.certification_notes' }),
      field({ key: 'supporting_attachment_references', label: 'Supporting Attachment References', officialName: 'Supporting Attachment References', type: 'textarea', aceMappingId: 'invoice.supporting_attachment_references' }),
    ],
  },
  {
    id: 'validation_reconciliation',
    title: 'Validation & Reconciliation',
    description: 'Capture missing customs content, valuation arithmetic, and cross-document discrepancies before the case moves forward.',
    fields: [
      field({ key: 'validation_status', label: 'Validation Status', officialName: 'Validation Status', type: 'text', aceMappingId: 'invoice.validation_status' }),
      field({ key: 'reconciliation_status', label: 'Reconciliation Status', officialName: 'Reconciliation Status', type: 'select', options: INVOICE_RECONCILIATION_STATUS_OPTIONS, aceMappingId: 'invoice.reconciliation_status' }),
      field({ key: 'risk_severity', label: 'Risk Severity', officialName: 'Risk Severity', type: 'select', options: INVOICE_RISK_OPTIONS, aceMappingId: 'invoice.risk_severity' }),
      field({ key: 'awa_review_summary', label: 'AWA Review Summary', officialName: 'AWA Review Summary', type: 'textarea', aceMappingId: 'invoice.awa_review_summary' }),
      field({ key: 'reconciliation_notes', label: 'Reconciliation Notes', officialName: 'Reconciliation Notes', type: 'textarea', aceMappingId: 'invoice.reconciliation_notes' }),
    ],
  },
  {
    id: 'audit_history',
    title: 'Audit / History',
    description: 'Track extraction provenance, OCR/system confidence, review state, edits, and comments for the invoice record.',
    fields: [
      field({ key: 'ocr_confidence', label: 'OCR Confidence', officialName: 'OCR Confidence', type: 'number', aceMappingId: 'invoice.ocr_confidence', validation: [NUMERIC_RULE] }),
      field({ key: 'extraction_source', label: 'Extraction Source', officialName: 'Extraction Source', type: 'text', aceMappingId: 'invoice.extraction_source' }),
      field({ key: 'agent_confirmed_status', label: 'Agent-Confirmed Status', officialName: 'Agent-Confirmed Status', type: 'select', options: YES_NO_OPTIONS, aceMappingId: 'invoice.agent_confirmed_status' }),
      field({ key: 'edited_by_agent_status', label: 'Edited-by-Agent Status', officialName: 'Edited-by-Agent Status', type: 'select', options: YES_NO_OPTIONS, aceMappingId: 'invoice.edited_by_agent_status' }),
      field({ key: 'last_edited_by', label: 'Last Edited By', officialName: 'Last Edited By', type: 'text', aceMappingId: 'invoice.last_edited_by' }),
      field({ key: 'last_edited_timestamp', label: 'Last Edited Timestamp', officialName: 'Last Edited Timestamp', type: 'text', placeholder: 'YYYY-MM-DD HH:MM', aceMappingId: 'invoice.last_edited_timestamp' }),
      field({ key: 'comments_notes', label: 'Comments / Notes', officialName: 'Comments / Notes', type: 'textarea', aceMappingId: 'invoice.comments_notes' }),
    ],
  },
]

export const COMMERCIAL_INVOICE_LINE_ITEM_FIELDS: DocumentFieldDefinition[] = [
  field({ key: 'line_number', label: 'Line Number', officialName: 'Line Number', type: 'text', required: true, aceMappingId: 'invoice.line.line_number', validation: [{ type: 'required', message: 'Line number is required.' }, { type: 'pattern', value: '^\\d{3}$', message: 'Use a three digit line number such as 001.' }] }),
  field({ key: 'part_number', label: 'Part Number / SKU / Style / Model', officialName: 'Part Number / SKU / Style / Model', type: 'text', aceMappingId: 'invoice.line.part_number' }),
  field({ key: 'detailed_merchandise_description', label: 'Detailed Description of Merchandise', officialName: 'Detailed Description of Merchandise', type: 'textarea', required: true, aceMappingId: 'invoice.line.description', validation: [{ type: 'required', message: 'Detailed merchandise description is required.' }] }),
  field({ key: 'grade_quality', label: 'Grade / Quality', officialName: 'Grade or Quality', type: 'text', aceMappingId: 'invoice.line.grade_quality' }),
  field({ key: 'marks_numbers_symbols', label: 'Marks / Numbers / Symbols', officialName: 'Marks, Numbers, and Symbols', type: 'textarea', aceMappingId: 'invoice.line.marks_numbers_symbols' }),
  field({ key: 'package_marks_numbers', label: 'Package Marks and Numbers', officialName: 'Marks and Numbers of Packages', type: 'textarea', aceMappingId: 'invoice.line.package_marks_numbers' }),
  field({ key: 'hts_tariff_number', label: 'HTS / Tariff Number', officialName: 'HTS / Tariff Number', type: 'text', aceMappingId: 'invoice.line.hts_tariff_number' }),
  field({ key: 'country_of_origin_line', label: 'Country of Origin by Line', officialName: 'Country of Origin', type: 'text', aceMappingId: 'invoice.line.country_of_origin' }),
  field({ key: 'quantity', label: 'Quantity', officialName: 'Quantity', type: 'number', aceMappingId: 'invoice.line.quantity', validation: [NUMERIC_RULE] }),
  field({ key: 'quantity_uom', label: 'Quantity Unit of Measure', officialName: 'Quantity Unit of Measure', type: 'text', aceMappingId: 'invoice.line.quantity_uom' }),
  field({ key: 'gross_weight', label: 'Gross Weight', officialName: 'Gross Weight', type: 'number', aceMappingId: 'invoice.line.gross_weight', validation: [NUMERIC_RULE] }),
  field({ key: 'net_weight', label: 'Net Weight', officialName: 'Net Weight', type: 'number', aceMappingId: 'invoice.line.net_weight', validation: [NUMERIC_RULE] }),
  field({ key: 'measurement_volume', label: 'Measurement / Volume', officialName: 'Measurement / Volume', type: 'number', aceMappingId: 'invoice.line.measurement_volume', validation: [NUMERIC_RULE] }),
  field({ key: 'unit_price', label: 'Unit Price', officialName: 'Purchase Price / Value', type: 'number', aceMappingId: 'invoice.line.unit_price', validation: [NUMERIC_RULE] }),
  field({ key: 'extended_line_value', label: 'Extended Line Value', officialName: 'Extended Line Value', type: 'number', aceMappingId: 'invoice.line.extended_line_value', validation: [NUMERIC_RULE] }),
  field({ key: 'currency_line', label: 'Currency', officialName: 'Currency', type: 'text', placeholder: 'USD', aceMappingId: 'invoice.line.currency', validation: [CURRENCY_RULE] }),
  field({ key: 'manufacturer_supplier_reference', label: 'Manufacturer / Supplier Reference', officialName: 'Manufacturer / Supplier Reference', type: 'text', aceMappingId: 'invoice.line.manufacturer_supplier_reference' }),
  field({ key: 'serial_lot_batch_number', label: 'Serial / Lot / Batch Number', officialName: 'Serial / Lot / Batch Number', type: 'text', aceMappingId: 'invoice.line.serial_lot_batch_number' }),
  field({ key: 'packaging_type', label: 'Packaging Type', officialName: 'Packaging Type', type: 'text', aceMappingId: 'invoice.line.packaging_type' }),
  field({ key: 'packaging_count', label: 'Packaging Count', officialName: 'Packaging Count', type: 'number', aceMappingId: 'invoice.line.packaging_count', validation: [NUMERIC_RULE] }),
  field({ key: 'assists_components_material_breakdown', label: 'Assists / Components / Material Breakdown', officialName: 'Assists / Components / Material Breakdown', type: 'textarea', aceMappingId: 'invoice.line.assists_components_material_breakdown', helpText: '19 CFR § 141.86(a)(11) requires goods or services furnished for production that are not included in the invoice price.' }),
  field({ key: 'line_discrepancy_status', label: 'Per-Line Discrepancy Status', officialName: 'Per-Line Discrepancy Status', type: 'select', options: INVOICE_RECONCILIATION_STATUS_OPTIONS, aceMappingId: 'invoice.line.discrepancy_status' }),
]

export const COMMERCIAL_INVOICE_FIELD_REGISTRY = [
  ...COMMERCIAL_INVOICE_SECTIONS.flatMap((section) =>
    section.kind === 'line_items'
      ? COMMERCIAL_INVOICE_LINE_ITEM_FIELDS.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        }))
      : section.fields.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        })),
  ),
]
